import {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import Select from 'react-select'
import {toast} from 'react-toastify'
import {useThemeMode} from '../../../../_metronic/partials'
import {
  BuyerOption,
  exitBuyerView,
  fetchBuyerOptions,
  getBuyerViewInfo,
  startBuyerView,
} from '../core/BuyerView'

type ModalProps = {show: boolean; onClose: () => void}

// Admin picks which buyer to view the app as (read-only)
export const BuyerViewModal: FC<ModalProps> = ({show, onClose}) => {
  const {mode} = useThemeMode()
  const isDark = mode === 'dark'
  const [buyers, setBuyers] = useState<BuyerOption[]>([])
  const [selected, setSelected] = useState<BuyerOption | null>(null)
  const [loading, setLoading] = useState(false)
  const [starting, setStarting] = useState(false)

  useEffect(() => {
    if (!show) return
    setSelected(null)
    setLoading(true)
    fetchBuyerOptions()
      .then(setBuyers)
      .catch((error) => {
        console.error('Buyer view: could not load buyers', error)
        toast.error('Could not load buyers')
      })
      .finally(() => setLoading(false))
  }, [show])

  const start = async () => {
    if (!selected) return
    setStarting(true)
    try {
      await startBuyerView(selected)
    } catch (error: any) {
      setStarting(false)
      toast.error(error?.response?.data?.message || 'Could not start buyer view')
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className='fs-3'>View as Buyer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted fs-7'>
          See the app exactly as the selected buyer sees it. This view is read-only and exports are
          disabled. Your admin session stays active, so you can switch back at any time.
        </p>
        <Select<BuyerOption>
          isLoading={loading}
          options={buyers}
          value={selected}
          onChange={(option) => setSelected(option)}
          getOptionValue={(option) => String(option.id)}
          getOptionLabel={(option) =>
            option.codes.length ? `${option.name} (${option.codes.join(', ')})` : option.name
          }
          placeholder='Select a buyer'
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({...base, zIndex: 9999}),
            ...(isDark && {
              control: (base, state) => ({
                ...base,
                backgroundColor: '#1b1b29',
                borderColor: state.isFocused ? '#009ef7' : '#323248',
                boxShadow: 'none',
                ':hover': {borderColor: state.isFocused ? '#009ef7' : '#565674'},
              }),
              singleValue: (base) => ({...base, color: '#e4e6ef'}),
              input: (base) => ({...base, color: '#e4e6ef'}),
              placeholder: (base) => ({...base, color: '#a1a5b7'}),
              indicatorSeparator: (base) => ({...base, backgroundColor: '#323248'}),
              menu: (base) => ({...base, backgroundColor: '#1b1b29', border: '1px solid #323248'}),
              option: (base, state) => ({
                ...base,
                color: '#e4e6ef',
                backgroundColor: state.isSelected
                  ? '#009ef7'
                  : state.isFocused
                  ? '#2b2b40'
                  : 'transparent',
              }),
            }),
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onClose} disabled={starting}>
          Cancel
        </button>
        <button type='button' className='btn btn-primary' onClick={start} disabled={!selected || starting}>
          {starting ? 'Switching...' : 'View as Buyer'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

// Shown on every page while an admin is viewing as a buyer
export const BuyerViewBanner: FC = () => {
  const info = getBuyerViewInfo()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [exiting, setExiting] = useState(false)
  if (!info) return null

  return (
    <>
      <div
        className='d-flex flex-wrap align-items-center justify-content-center gap-3 px-4 py-2 bg-warning text-dark fs-7 fw-bold'
        style={{position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100}}
      >
        <span>Viewing as buyer: {info.name} (read-only)</span>
        <button type='button' className='btn btn-sm btn-light' onClick={() => setPickerOpen(true)}>
          Switch buyer
        </button>
        <button
          type='button'
          className='btn btn-sm btn-dark'
          disabled={exiting}
          onClick={() => {
            setExiting(true)
            exitBuyerView()
          }}
        >
          {exiting ? 'Returning...' : 'Return to Admin'}
        </button>
      </div>
      <BuyerViewModal show={pickerOpen} onClose={() => setPickerOpen(false)} />
    </>
  )
}
